import { ShoppingCart } from "lucide-react";
import { MenuItem } from "@/hooks/usePublicMenu";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice } from "@/utils/currency";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getTagLabel } from "@/types/webMenu";

type Props = {
  item: MenuItem;
  onClick?: (item: MenuItem) => void;
  availableTags?: { key: string; label: string }[];
};

export default function MenuItemCard({ item, onClick, availableTags = [] }: Props) {
  const { language, t } = useLanguage();
  const formatPrice = useFormatPrice();
  const name = language === "ar" ? item.nameAr : item.name;
  const desc = language === "ar" ? item.descriptionAr : item.description;

    const getLabel = (tagKey: string) =>
    availableTags.find((t) => t.key === tagKey)?.label ?? tagKey;

  return (
    <article
      className="group relative flex flex-col rounded-xl overflow-hidden border border-border bg-card transition-all duration-200 hover:shadow-lg cursor-pointer"
      onClick={() => onClick?.(item)}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={item.image}
          alt={name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        {item.popular && (
          <Badge className="absolute top-2 start-2 bg-primary text-primary-foreground text-xs">
            🔥 Popular
          </Badge>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-heading font-bold text-foreground line-clamp-1">{name}</h3>
        <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{desc}</p>

        {/* Tags */}
        {item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags.map((tag) => (
              <Badge key={tag.key} variant="outline" className="text-[10px]">
                {getLabel(tag.key)}
              </Badge>
            ))}
          </div>
        )}

        {/* Price + CTA */}
        <div className="flex items-center justify-between mt-auto pt-3">
          <span className="font-heading text-lg font-bold text-primary">{formatPrice(item.price)}</span>
          {onClick && (
            <Button
              size="sm"
              className="gap-1.5"
              onClick={(e) => {
                e.stopPropagation();
                onClick(item);
              }}
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("menu.addToCart")}</span>
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
